import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { useCompanyInfo } from '../hooks/useAPI';

const PrivacyPolicyPage = () => {
  const { data: companyInfo } = useCompanyInfo();
  const company = companyInfo?.companyName || 'LionTek';
  const email = companyInfo?.email || 'hello@liontekglobal.com';

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Privacy Policy | LionTek</title>
        <meta name="description" content="LionTek's Privacy Policy — how we collect, use, and protect your personal information." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://liontekglobal.com/privacy-policy" />
      </Helmet>
      <Header companyInfo={companyInfo} />

      <main className="pt-24 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">

          {/* Header */}
          <div className="mb-10">
            <Link to="/" className="text-sm text-lion-blue hover:text-lion-yellow transition-colors mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-4xl font-poppins font-bold text-lion-blue mb-3">
              Privacy Policy
            </h1>
            <p className="text-gray-500 text-sm">Last updated: March 2026</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 space-y-10 text-gray-700 leading-relaxed">

            <section>
              <p>
                {company} ("we", "our", "us") is committed to protecting your personal information.
                This Privacy Policy explains what data we collect, how we use it, and your rights
                regarding your information when you visit <strong>liontekglobal.com</strong> or
                interact with our services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">1. Information We Collect</h2>
              <p className="mb-3">We collect information you provide directly to us, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contact form submissions</strong> — name, email address, phone number, company name, subject, and message.</li>
                <li><strong>Newsletter subscriptions</strong> — email address and subscription preferences.</li>
                <li><strong>Job applications</strong> — first name, last name, email address, phone number, cover letter, CV/resume, portfolio links, and LinkedIn/GitHub profiles.</li>
              </ul>
              <p className="mt-4">
                We also automatically collect certain technical data when you visit our website,
                including IP address, browser type, operating system, pages visited, and time spent on the site.
                This is collected through cookies and server logs.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To respond to your enquiries and contact form submissions.</li>
                <li>To send you newsletters and company updates you have subscribed to.</li>
                <li>To process and manage job applications.</li>
                <li>To improve our website and services through analytics.</li>
                <li>To comply with legal obligations.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">3. Data Storage and Security</h2>
              <p className="mb-3">
                Your data is stored securely on our servers. We implement appropriate technical and
                organisational measures to protect your personal information against unauthorised access,
                alteration, disclosure, or destruction.
              </p>
              <p>
                We retain contact form submissions and job application data for up to <strong>24 months</strong>.
                Newsletter subscriber data is retained until you unsubscribe.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">4. Third-Party Services</h2>
              <p className="mb-3">We use the following third-party services that may process your data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Email delivery (SMTP)</strong> — to send transactional and notification emails.</li>
                <li><strong>Hosting provider</strong> — our servers are hosted on a secure infrastructure provider.</li>
              </ul>
              <p className="mt-4">
                We do not sell, trade, or rent your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">5. Cookies</h2>
              <p>
                We use cookies to enhance your browsing experience. You can manage your cookie
                preferences at any time through the cookie banner displayed on our site. For full
                details, please read our{' '}
                <Link to="/cookie-policy" className="text-lion-blue hover:text-lion-yellow underline">
                  Cookie Policy
                </Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">6. Your Rights</h2>
              <p className="mb-3">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access the personal data we hold about you.</li>
                <li>Request correction of inaccurate data.</li>
                <li>Request deletion of your personal data.</li>
                <li>Object to or restrict how we process your data.</li>
                <li>Withdraw consent at any time (e.g. unsubscribe from newsletter).</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, contact us at{' '}
                <a href={`mailto:${email}`} className="text-lion-blue hover:text-lion-yellow underline">
                  {email}
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">7. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we do, we will update the
                "Last updated" date at the top of this page. We encourage you to review this policy
                periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">8. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-lion-blue">{company}</p>
                <p>{companyInfo?.address || 'Africa'}</p>
                <p>
                  <a href={`mailto:${email}`} className="text-lion-blue hover:text-lion-yellow underline">
                    {email}
                  </a>
                </p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer companyInfo={companyInfo} />
    </div>
  );
};

export default PrivacyPolicyPage;
